import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { orderAPI, checkoutAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/layout/Navigation';
import CartItemCustomizer from '../components/CartItemCustomizer';
import { Check, ChevronRight, Package, MapPin, FileText, CreditCard } from 'lucide-react';

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = location.state?.orderId;
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Wizard steps: 1 = Cards, 2 = Shipping, 3 = Review, 4 = Payment
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Card Customization
  const [customizingItemIndex, setCustomizingItemIndex] = useState(null);
  const [itemCustomizations, setItemCustomizations] = useState({});
  
  // Step 2: Shipping Address
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: ''
  });
  const [shippingErrors, setShippingErrors] = useState({});
  
  // Step 3: Comments
  const [deckLink, setDeckLink] = useState('');
  const [generalComments, setGeneralComments] = useState('');
  
  // Discount code
  const [discountCode, setDiscountCode] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  useEffect(() => {
    if (!orderId) {
      navigate('/cart');
      return;
    }
    fetchOrder();
  }, [orderId, navigate]);

  const fetchOrder = async () => {
    try {
      const response = await orderAPI.getById(orderId);
      setOrder(response.data);
      // Load applied discount if exists
      if (response.data.discount) {
        setAppliedDiscount(response.data.discount);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomizationSave = async (itemIndex, customization) => {
    try {
      // Update order item customization in backend
      await api.patch(`/orders/${orderId}/items/${itemIndex}/customization`, customization);
      
      // Update local state
      setItemCustomizations(prev => ({
        ...prev,
        [itemIndex]: customization
      }));
      
      setCustomizingItemIndex(null);
    } catch (error) {
      console.error('Failed to save customization:', error);
      alert(t('checkout.saveFailed') || 'Failed to save customization');
    }
  };

  // Check if all items are customized
  const allItemsCustomized = () => {
    if (!order) return false;
    return order.items.every((_, index) => itemCustomizations[index]);
  };

  // Validate Step 1
  const canProceedFromStep1 = () => {
    return allItemsCustomized();
  };

  // Validate Step 2
  const canProceedFromStep2 = () => {
    const errors = {};
    if (!shippingAddress.fullName || shippingAddress.fullName.trim() === '') {
      errors.fullName = t('checkout.required') || 'Required';
    }
    if (!shippingAddress.address || shippingAddress.address.trim() === '') {
      errors.address = t('checkout.required') || 'Required';
    }
    if (!shippingAddress.city || shippingAddress.city.trim() === '') {
      errors.city = t('checkout.required') || 'Required';
    }
    if (!shippingAddress.postalCode || shippingAddress.postalCode.trim() === '') {
      errors.postalCode = t('checkout.required') || 'Required';
    }
    if (!shippingAddress.country || shippingAddress.country.trim() === '') {
      errors.country = t('checkout.required') || 'Required';
    }
    
    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !canProceedFromStep1()) {
      alert(t('checkout.mustCustomizeAll') || 'You must configure all cards before continuing');
      return;
    }
    if (currentStep === 2 && !canProceedFromStep2()) {
      // Errors are already set by canProceedFromStep2
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Por favor ingresa un código');
      return;
    }

    setApplyingDiscount(true);
    setDiscountError('');

    try {
      await api.post(`/orders/${orderId}/apply-discount`, {
        code: discountCode,
        order_amount: order?.total_amount || 0
      });

      // Refresh order to get updated total and discount
      const updatedOrder = await orderAPI.getById(orderId);
      setOrder(updatedOrder.data);
      // Set appliedDiscount from the order's discount field
      if (updatedOrder.data.discount) {
        setAppliedDiscount(updatedOrder.data.discount);
      }
      setDiscountCode('');
      setDiscountError(''); // Clear any previous errors
    } catch (error) {
      console.error('Failed to apply discount:', error);
      setDiscountError(error.response?.data?.detail || 'Código no válido');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    try {
      await api.delete(`/orders/${orderId}/discount`);
      setAppliedDiscount(null);
      // Refresh order
      const updatedOrder = await orderAPI.getById(orderId);
      setOrder(updatedOrder.data);
    } catch (error) {
      console.error('Failed to remove discount:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await checkoutAPI.createSession({
        order_id: orderId,
        origin_url: originUrl,
        shipping_address: shippingAddress,
        deck_link: deckLink || null
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('checkout.error') || 'Failed to create checkout session');
      setProcessing(false);
    }
  };

  const steps = [
    { number: 1, name: t('checkout.step1') || 'Card Customization', icon: Package },
    { number: 2, name: t('checkout.step2') || 'Shipping Address', icon: MapPin },
    { number: 3, name: t('checkout.step3') || 'Review & Comments', icon: FileText },
    { number: 4, name: t('checkout.step4') || 'Payment', icon: CreditCard }
  ];

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-brand-bg pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-12">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-outfit font-light tracking-tight text-white mb-8">
            {t('checkout.title') || 'Checkout'}
          </h1>

          {/* Step Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = step.number < currentStep;
                const isCurrent = step.number === currentStep;
                
                return (
                  <React.Fragment key={step.number}>
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                          isCompleted
                            ? 'bg-green-600 text-white'
                            : isCurrent
                            ? 'bg-violet-600 text-white ring-4 ring-violet-600/30'
                            : 'bg-zinc-800 text-zinc-500'
                        }`}
                      >
                        {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                      </div>
                      <p
                        className={`text-sm mt-2 text-center font-medium ${
                          isCurrent ? 'text-white' : 'text-zinc-500'
                        }`}
                      >
                        {step.name}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 h-0.5 bg-zinc-800 mx-2 mb-8">
                        <div
                          className={`h-full transition-all ${
                            isCompleted ? 'bg-green-600' : 'bg-zinc-800'
                          }`}
                          style={{ width: isCompleted ? '100%' : '0%' }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-brand-surface border border-brand-border rounded-xl p-8">
            {/* STEP 1: Card Customization */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-2xl font-outfit font-semibold text-white mb-2">
                  {t('checkout.customizeYourCards') || 'Customize Your Cards'}
                </h2>
                <p className="text-zinc-400 mb-6">
                  {t('checkout.customizeDesc') || 'Configure each card with specific set and artwork'}
                </p>

                <div className="space-y-4">
                  {order?.items.map((item, idx) => {
                    const hasCustomization = itemCustomizations[idx];
                    const cardCount = hasCustomization?.cards?.length || 0;
                    const configuredCount = hasCustomization?.cards?.filter(c => c.selected_version || c.custom_image).length || 0;

                    return (
                      <div
                        key={idx}
                        className={`border-2 rounded-lg p-6 transition-all ${
                          hasCustomization
                            ? 'border-green-600 bg-green-900/10'
                            : 'border-yellow-600 bg-yellow-900/10'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-white mb-1">
                              {item.product_name}
                            </h3>
                            <p className="text-sm text-zinc-400">
                              {t('checkout.quantity')}: {item.quantity} • {item.price.toFixed(2)}€
                            </p>
                            {hasCustomization && (
                              <p className="text-sm text-green-400 mt-2">
                                ✓ {configuredCount} / {cardCount} {t('checkout.cardsConfigured') || 'cards configured'}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => setCustomizingItemIndex(idx)}
                            className={`px-6 py-3 rounded-lg font-bold transition-all flex items-center space-x-2 ${
                              hasCustomization
                                ? 'bg-zinc-800 text-white hover:bg-zinc-700'
                                : 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-500 hover:to-purple-500 animate-pulse'
                            }`}
                          >
                            <Package size={18} />
                            <span>
                              {hasCustomization
                                ? t('checkout.editCards') || 'Edit Cards'
                                : t('checkout.configureCards') || 'Configure Cards'}
                            </span>
                          </button>
                        </div>

                        {!hasCustomization && (
                          <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4">
                            <p className="text-yellow-400 text-sm font-medium">
                              ⚠ {t('checkout.mustConfigureItem') || 'You must configure this item to continue'}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Progress Summary */}
                <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">
                      {t('checkout.overallProgress') || 'Overall Progress'}
                    </span>
                    <span className="text-zinc-400">
                      {Object.keys(itemCustomizations).length} / {order?.items.length} {t('checkout.itemsReady') || 'items ready'}
                    </span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-3 mt-3">
                    <div
                      className="bg-gradient-to-r from-violet-600 to-purple-600 h-3 rounded-full transition-all"
                      style={{
                        width: `${(Object.keys(itemCustomizations).length / order?.items.length) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: Shipping Address */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-outfit font-semibold text-white mb-2">
                  {t('checkout.shippingAddress') || 'Shipping Address'}
                </h2>
                <p className="text-zinc-400 mb-6">
                  {t('checkout.shippingDesc') || 'Where should we send your cards?'}
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      {t('checkout.fullName') || 'Full Name'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.fullName}
                      onChange={(e) => {
                        setShippingAddress({ ...shippingAddress, fullName: e.target.value });
                        if (shippingErrors.fullName) {
                          setShippingErrors({ ...shippingErrors, fullName: null });
                        }
                      }}
                      className={`w-full bg-zinc-900 border ${shippingErrors.fullName ? 'border-red-500' : 'border-zinc-800'} text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all`}
                      placeholder="John Doe"
                    />
                    {shippingErrors.fullName && (
                      <p className="text-red-400 text-sm mt-1">{shippingErrors.fullName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      {t('checkout.address') || 'Address'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.address}
                      onChange={(e) => {
                        setShippingAddress({ ...shippingAddress, address: e.target.value });
                        if (shippingErrors.address) {
                          setShippingErrors({ ...shippingErrors, address: null });
                        }
                      }}
                      className={`w-full bg-zinc-900 border ${shippingErrors.address ? 'border-red-500' : 'border-zinc-800'} text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all`}
                      placeholder="123 Main Street, Apt 4B"
                    />
                    {shippingErrors.address && (
                      <p className="text-red-400 text-sm mt-1">{shippingErrors.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        {t('checkout.city') || 'City'} *
                      </label>
                      <input
                        type="text"
                        required
                        value={shippingAddress.city}
                        onChange={(e) => {
                          setShippingAddress({ ...shippingAddress, city: e.target.value });
                          if (shippingErrors.city) {
                            setShippingErrors({ ...shippingErrors, city: null });
                          }
                        }}
                        className={`w-full bg-zinc-900 border ${shippingErrors.city ? 'border-red-500' : 'border-zinc-800'} text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all`}
                        placeholder="Madrid"
                      />
                      {shippingErrors.city && (
                        <p className="text-red-400 text-sm mt-1">{shippingErrors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        {t('checkout.postalCode') || 'Postal Code'} *
                      </label>
                      <input
                        type="text"
                        required
                        value={shippingAddress.postalCode}
                        onChange={(e) => {
                          setShippingAddress({ ...shippingAddress, postalCode: e.target.value });
                          if (shippingErrors.postalCode) {
                            setShippingErrors({ ...shippingErrors, postalCode: null });
                          }
                        }}
                        className={`w-full bg-zinc-900 border ${shippingErrors.postalCode ? 'border-red-500' : 'border-zinc-800'} text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all`}
                        placeholder="28001"
                      />
                      {shippingErrors.postalCode && (
                        <p className="text-red-400 text-sm mt-1">{shippingErrors.postalCode}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      {t('checkout.country') || 'Country'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.country}
                      onChange={(e) => {
                        setShippingAddress({ ...shippingAddress, country: e.target.value });
                        if (shippingErrors.country) {
                          setShippingErrors({ ...shippingErrors, country: null });
                        }
                      }}
                      className={`w-full bg-zinc-900 border ${shippingErrors.country ? 'border-red-500' : 'border-zinc-800'} text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all`}
                      placeholder="Spain"
                    />
                    {shippingErrors.country && (
                      <p className="text-red-400 text-sm mt-1">{shippingErrors.country}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      {t('checkout.phone') || 'Phone'}
                    </label>
                    <input
                      type="tel"
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                      placeholder="+34 600 000 000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Review & Comments */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-2xl font-outfit font-semibold text-white mb-2">
                  {t('checkout.reviewOrder') || 'Review Your Order'}
                </h2>
                <p className="text-zinc-400 mb-6">
                  {t('checkout.reviewDesc') || 'Check everything before payment'}
                </p>

                {/* Order Summary */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {t('checkout.orderSummary') || 'Order Summary'}
                  </h3>
                  <div className="space-y-3">
                    {order?.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm pb-3 border-b border-zinc-800">
                        <div>
                          <span className="text-white font-medium block">{item.product_name}</span>
                          <span className="text-zinc-500 text-xs">
                            {itemCustomizations[idx]?.cards?.length || 0} {t('checkout.cards') || 'cards'} • {t('checkout.quantity')}: {item.quantity}
                          </span>
                        </div>
                        <span className="text-white font-medium">{(item.price * item.quantity).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Subtotal and Discount */}
                  {(order?.discount || order?.subtotal) && (
                    <>
                      <div className="flex justify-between items-center pt-3 mt-3 text-zinc-400">
                        <span>{t('checkout.subtotal') || 'Subtotal'}</span>
                        <span>{order?.subtotal?.toFixed(2)}€</span>
                      </div>
                      {order?.discount && (
                        <div className="flex justify-between items-center pt-2 text-green-400">
                          <span>{t('checkout.discount') || 'Descuento'} ({order.discount.code})</span>
                          <span>- {order.discount.discount_amount.toFixed(2)}€</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="flex justify-between items-center pt-4 border-t border-zinc-700 mt-4">
                    <span className="text-lg font-outfit font-semibold text-white">
                      {t('checkout.total') || 'Total'}
                    </span>
                    <span className="text-2xl font-bold text-violet-600">{order?.total_amount.toFixed(2)}€</span>
                  </div>
                </div>

                {/* Discount Code */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    🎟️ {t('checkout.discountCode') || 'Código de Descuento'}
                  </h3>
                  
                  {appliedDiscount || order?.discount ? (
                    <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-400 font-semibold">
                            ✓ {(appliedDiscount || order?.discount)?.code}
                          </p>
                          <p className="text-zinc-400 text-sm mt-1">
                            {(appliedDiscount || order?.discount)?.discount_type === 'percentage' 
                              ? `${(appliedDiscount || order?.discount)?.discount_value}% de descuento`
                              : `${(appliedDiscount || order?.discount)?.discount_value}€ de descuento`
                            }
                          </p>
                          <p className="text-green-400 text-sm font-bold mt-2">
                            - {(appliedDiscount || order?.discount)?.discount_amount?.toFixed(2)}€
                          </p>
                        </div>
                        <button
                          onClick={handleRemoveDiscount}
                          className="px-3 py-2 bg-red-600/20 text-red-400 border border-red-600/30 rounded-md text-sm hover:bg-red-600/30 transition-all"
                        >
                          {t('checkout.remove') || 'Remover'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => {
                          setDiscountCode(e.target.value.toUpperCase());
                          setDiscountError('');
                        }}
                        placeholder="CODIGO2024"
                        disabled={applyingDiscount}
                        className="flex-1 bg-zinc-950 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all disabled:opacity-50 uppercase"
                      />
                      <button
                        onClick={handleApplyDiscount}
                        disabled={applyingDiscount || !discountCode.trim()}
                        className="px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {applyingDiscount 
                          ? (t('checkout.applying') || 'Aplicando...')
                          : (t('checkout.apply') || 'Aplicar')
                        }
                      </button>
                    </div>
                  )}
                  
                  {discountError && (
                    <p className="text-red-400 text-sm mt-3">
                      ❌ {discountError}
                    </p>
                  )}
                </div>

                {/* Shipping Address Review */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('checkout.shippingTo') || 'Shipping To'}
                  </h3>
                  <div className="text-zinc-300 text-sm space-y-1">
                    <p className="font-medium text-white">{shippingAddress.fullName}</p>
                    <p>{shippingAddress.address}</p>
                    <p>{shippingAddress.city}, {shippingAddress.postalCode}</p>
                    <p>{shippingAddress.country}</p>
                    {shippingAddress.phone && <p>{shippingAddress.phone}</p>}
                  </div>
                </div>

                {/* Optional Comments */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      🔗 {t('checkout.deckLink') || 'Deck Link'} ({t('checkout.optional') || 'Optional'})
                    </label>
                    <input
                      type="url"
                      value={deckLink}
                      onChange={(e) => setDeckLink(e.target.value)}
                      placeholder="https://moxfield.com/decks/your-deck"
                      className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                    />
                    <p className="text-xs text-zinc-500 mt-2">
                      {t('checkout.deckLinkHint') || 'Share your Moxfield or Archidekt URL'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      💬 {t('checkout.generalComments') || 'General Comments'} ({t('checkout.optional') || 'Optional'})
                    </label>
                    <textarea
                      value={generalComments}
                      onChange={(e) => setGeneralComments(e.target.value)}
                      placeholder={t('checkout.generalCommentsPlaceholder') || 'Any additional notes or special requests...'}
                      rows={4}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Payment (just a button, redirects to Stripe) */}
            {currentStep === 4 && (
              <div className="text-center py-8">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CreditCard size={40} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-outfit font-semibold text-white mb-2">
                    {t('checkout.readyToPay') || 'Ready to Complete Your Order'}
                  </h2>
                  <p className="text-zinc-400">
                    {t('checkout.redirectStripe') || 'You will be redirected to Stripe for secure payment'}
                  </p>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md mx-auto mb-8">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">{t('checkout.totalAmount') || 'Total Amount'}</span>
                    <span className="text-3xl font-bold text-violet-600">{order?.total_amount.toFixed(2)}€</span>
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={processing}
                  className="px-12 py-5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-violet-500 hover:to-purple-500 transition-all shadow-2xl hover:shadow-violet-500/50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-3"
                >
                  <CreditCard size={24} />
                  <span>{processing ? t('checkout.redirecting') : t('checkout.proceedToPayment') || 'Proceed to Payment'}</span>
                  <ChevronRight size={24} />
                </button>
                <p className="text-xs text-zinc-500 mt-4">
                  {t('checkout.securePayment') || 'Secure payment powered by Stripe'}
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep < 4 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-zinc-800">
                <button
                  onClick={handlePreviousStep}
                  disabled={currentStep === 1}
                  className="px-6 py-3 bg-zinc-800 text-white rounded-lg font-medium hover:bg-zinc-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {t('checkout.previous') || 'Previous'}
                </button>

                <button
                  onClick={handleNextStep}
                  className="px-8 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-bold hover:from-violet-500 hover:to-purple-500 transition-all flex items-center space-x-2"
                >
                  <span>{t('checkout.continue') || 'Continue'}</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customization Modal */}
      {customizingItemIndex !== null && order?.items[customizingItemIndex] && (
        <CartItemCustomizer
          item={order.items[customizingItemIndex]}
          existingCustomization={itemCustomizations[customizingItemIndex] || order.items[customizingItemIndex].customization}
          onSave={(customization) => handleCustomizationSave(customizingItemIndex, customization)}
          onClose={() => setCustomizingItemIndex(null)}
        />
      )}
    </>
  );
};

export default Checkout;
